import * as React from "react";
import { Box, Typography, IconButton, InputAdornment, Alert, Link, Divider, Button } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../authContext/authContext";
import { loginVerifyOtp } from "../../Services/authService";
import AuthLayout from "../../components/AuthLayout";
import DarkTextField from "../../components/DarkTextField";
import AuthPrimaryButton from "../../components/AuthPrimaryButton";
import OtpBoxes from "../../components/OtpBoxes";
import {
  AUTH_TEXT_PRIMARY,
  AUTH_TEXT_SECONDARY,
  AUTH_CARD_BORDER,
  AUTH_ERROR,
  AUTH_SUCCESS,
  BRAND_BLUE,
} from "../../theme/appTheme";
import { useTranslation } from "../../i18n/LanguageContext";
import { consumeLogoutReason } from "../../Services/sessionStorage";

function Login() {
  const navigate = useNavigate();
  const { login, applySession } = useAuth();
  const { t } = useTranslation();

  const [step, setStep] = React.useState("credentials"); // credentials | otp

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [emailError, setEmailError] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  // Pre-filled when apiFetch signed the user out because the account was
  // deactivated mid-session (see markLogoutReason) — shown once, then gone.
  const [loginError, setLoginError] = React.useState(() =>
    consumeLogoutReason() === "inactive" ? t("auth.login.errors.inactive") : ""
  );
  // Customer with orders but no portal account yet — offer registration
  // with the email prefilled instead of a dead-end error.
  const [needsRegistration, setNeedsRegistration] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  // --- OTP step (patient/therapist accounts only - admin/operator never
  // see this). The 6-digit code is in the login email and typed in here.
  const [otp, setOtp] = React.useState("");
  const [otpError, setOtpError] = React.useState("");
  const [otpLoading, setOtpLoading] = React.useState(false);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [resendNotice, setResendNotice] = React.useState("");

  const handleTogglePassword = () => setShowPassword((prev) => !prev);

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async () => {
    // Enter + click (or a double click) would otherwise fire two logins and
    // email two different codes for the same sign-in.
    if (loading) return;
    let valid = true;

    if (!email) {
      setEmailError(t("auth.login.emailRequired"));
      valid = false;
    } else if (!validateEmail(email)) {
      setEmailError(t("auth.login.invalidEmail"));
      valid = false;
    } else {
      setEmailError("");
    }

    if (!password) {
      setPasswordError(t("auth.login.passwordRequired"));
      valid = false;
    } else {
      setPasswordError("");
    }

    if (!valid) return;

    setLoginError("");
    setNeedsRegistration(false);
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.mfaRequired) {
        setOtp("");
        setOtpError("");
        setResendNotice("");
        setStep("otp");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      if (/please register/i.test(error.message || "")) {
        // Shadow account (has orders, never registered) - distinct from wrong-password.
        setLoginError(t("auth.login.errors.shadowAccount"));
        setNeedsRegistration(true);
      } else if (error.status === 400) {
        setLoginError(t("auth.login.errors.badRequest"));
      } else if (error.status === 401) {
        setLoginError(t("auth.login.errors.invalidCredentials"));
      } else if (error.status === 403) {
        setLoginError(t("auth.login.errors.inactive"));
      } else if (error.status === 429) {
        setLoginError(t("auth.login.errors.tooMany"));
      } else {
        setLoginError(t("auth.login.errors.generic"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep("credentials");
    setPassword("");
    setOtp("");
    setOtpError("");
    setResendNotice("");
  };

  const handleVerifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) return;
    setOtpLoading(true);
    setOtpError("");
    try {
      const data = await loginVerifyOtp(email, otp);
      applySession(data);
      navigate("/dashboard");
    } catch (error) {
      setOtpError(error.status === 429 ? t("auth.login.errors.tooMany") : t("auth.login.otp.incorrectCode"));
    } finally {
      setOtpLoading(false);
    }
  };

  // No dedicated resend endpoint - re-submitting the original credentials
  // gets a fresh code emailed the same way the first one was.
  const handleResend = async () => {
    if (resendLoading) return;
    setResendLoading(true);
    setOtpError("");
    setResendNotice("");
    try {
      await login(email, password);
      setOtp("");
      setResendNotice(t("auth.login.otp.resendNotice"));
    } catch (error) {
      setOtpError(error.message || t("auth.login.otp.resendError"));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <AuthLayout>
      {step === "credentials" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: "1.0625rem", color: AUTH_TEXT_PRIMARY }}>{t("auth.login.title")}</Typography>
          <Typography sx={{ mb: 3, fontSize: "0.8125rem", color: AUTH_TEXT_SECONDARY }}>
            {t("auth.login.subtitle")}
          </Typography>

          <DarkTextField
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("auth.login.emailPlaceholder")}
            autoFocus
          />
          {emailError && (
            <Typography variant="caption" sx={{ color: AUTH_ERROR, mt: 0.5, display: "block" }}>
              {emailError}
            </Typography>
          )}

          <DarkTextField
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type={showPassword ? "text" : "password"}
            placeholder={t("auth.login.passwordPlaceholder")}
            sx={{ mt: 1.5 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleTogglePassword} edge="end" size="small" sx={{ color: "#6b7280" }}>
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {passwordError && (
            <Typography variant="caption" sx={{ color: AUTH_ERROR, mt: 0.5, display: "block" }}>
              {passwordError}
            </Typography>
          )}

          <Box sx={{ textAlign: "right", mt: 1 }}>
            <Link component={RouterLink} to="/forgot-password" sx={{ fontSize: "0.75rem", color: BRAND_BLUE }}>
              {t("auth.login.forgotPassword")}
            </Link>
          </Box>

          {loginError && (
            <Alert
              severity={needsRegistration ? "info" : "error"}
              sx={{ mt: 2 }}
              action={
                needsRegistration ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => navigate(`/register?email=${encodeURIComponent(email.trim())}`)}
                    sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
                  >
                    {t("auth.login.createAccount")}
                  </Button>
                ) : null
              }
            >
              {loginError}
            </Alert>
          )}

          <AuthPrimaryButton type="submit" disabled={loading} sx={{ mt: 2.5 }}>
            {loading ? t("auth.login.submitting") : t("auth.login.submit")}
          </AuthPrimaryButton>

          <Divider sx={{ my: 2.5, borderColor: AUTH_CARD_BORDER }} />

          <Typography sx={{ textAlign: "center", fontSize: "0.8125rem", color: AUTH_TEXT_SECONDARY }}>
            {t("auth.login.newToSibolab")}{" "}
            <Link component={RouterLink} to="/register" sx={{ color: BRAND_BLUE }}>
              {t("auth.login.createAccount")}
            </Link>
          </Typography>
        </form>
      )}

      {step === "otp" && (
        <Box>
          <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: "1.0625rem", color: AUTH_TEXT_PRIMARY, textAlign: "center" }}>
            {t("auth.login.otp.title")}
          </Typography>
          <Typography sx={{ mb: 3, fontSize: "0.8125rem", color: AUTH_TEXT_SECONDARY, textAlign: "center" }}>
            {t("auth.login.otp.subtitle").split("{{email}}")[0]}
            <strong>{email}</strong>
            {t("auth.login.otp.subtitle").split("{{email}}")[1]}
          </Typography>

          <OtpBoxes value={otp} onChange={setOtp} error={!!otpError} />

          {otpError && (
            <Typography variant="caption" sx={{ color: AUTH_ERROR, mt: 2, display: "block", textAlign: "center" }}>
              {otpError}
            </Typography>
          )}
          {resendNotice && !otpError && (
            <Typography variant="caption" sx={{ color: AUTH_SUCCESS, mt: 2, display: "block", textAlign: "center" }}>
              {resendNotice}
            </Typography>
          )}

          <AuthPrimaryButton onClick={handleVerifyOtp} disabled={otpLoading || !/^\d{6}$/.test(otp)} sx={{ mt: 3 }}>
            {otpLoading ? t("auth.login.otp.verifying") : t("auth.login.otp.verify")}
          </AuthPrimaryButton>

          <Alert severity="info" icon={false} sx={{ mt: 2.5, fontSize: "0.75rem", py: 0.25, justifyContent: "center", textAlign: "center" }}>
            {t("auth.emailHint")}
          </Alert>

          <Typography sx={{ mt: 2.5, textAlign: "center", fontSize: "0.75rem", color: AUTH_TEXT_SECONDARY }}>
            {t("auth.login.otp.notSeeing")}{" "}
            <Link component="button" type="button" onClick={handleResend} disabled={resendLoading} sx={{ color: BRAND_BLUE, fontSize: "0.75rem" }}>
              {resendLoading ? t("auth.login.otp.sending") : t("auth.login.otp.sendNewCode")}
            </Link>
            .{" "}
            {t("auth.login.otp.wrongEmail")}{" "}
            <Link component="button" type="button" onClick={handleBackToCredentials} sx={{ color: BRAND_BLUE, fontSize: "0.75rem" }}>
              {t("auth.login.otp.changeEmail")}
            </Link>
            .
          </Typography>
        </Box>
      )}
    </AuthLayout>
  );
}

export default Login;
